import { useState, useMemo, useCallback, useEffect } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { format, addDays, addWeeks, startOfISOWeek, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  UserCheck, ChevronLeft, ChevronRight, Calendar, RefreshCw, AlertTriangle,
  Users, Sun, Moon, CalendarDays, CalendarOff, DoorOpen, DoorClosed, Clock,
} from 'lucide-react';
import {
  useOrganization, useAssignOrganization, useResetOrganization, useEmployees, useOrgTimeline,
  usePharmacySettings,
} from '@/api/resources';
import type { HalfDayTimeline } from '@/api/resources';
import { useCounterReadOnly } from '@/lib/useCounterSession';
import { weekDayCount } from '@/lib/schedule';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import type { MissionType, Employee, Skill } from '@/types';

type ViewMode = 'day' | 'week';

interface Assignment {
  mission: MissionType;
  employeeIds: string[];
  minCount?: number;
  targetCount: number;
  status: 'COVERED' | 'PARTIAL' | 'UNCOVERED';
  presence?: boolean;     // ouverture/fermeture : exigence de présence, pas un poste
  presentCount?: number;
  eligibleCount?: number; // présents compétents pour cette mission
}

interface ProposalData {
  assignments: Assignment[];
  presentEmployeeIds: string[];
  alerts: { type: 'CRITICAL' | 'WARNING' | 'INFO'; message: string; mission?: MissionType }[];
  source?: 'AUTO' | 'PERSISTED';
  closed?: boolean;       // pharmacie fermée ce créneau
  holidayName?: string;   // fermée parce que férié (Paramètres › Jours fériés)
}

const MISSION_LABELS: Record<string, string> = {
  COMPTOIR: 'Comptoir',
  BACKOFFICE_PHARMA: 'Back office pharma',
  BACKOFFICE_TECHNIQUE: 'Back office technique',
  VACCINATION: 'Vaccination',
  PDA: 'PDA',
  PREPARATOIRE: 'Préparatoire',
  TROD: 'TROD',
  ENTRETIEN: 'Entretiens',
  BILAN: 'Bilans',
  OUVERTURE: 'Ouverture',
  FERMETURE: 'Fermeture',
};

// Compétence requise par mission (miroir du moteur backend). null = aucune.
const MISSION_SKILL: Record<string, Skill | null> = {
  COMPTOIR: 'COMPTOIR',
  BACKOFFICE_PHARMA: 'BACKOFFICE_PHARMA',
  BACKOFFICE_TECHNIQUE: 'BACKOFFICE_TECHNIQUE',
  VACCINATION: 'VACCINATION',
  PDA: 'PDA',
  PREPARATOIRE: 'PREPARATOIRE',
  TROD: 'TROD',
  ENTRETIEN: 'ENTRETIEN',
  BILAN: 'BILAN',
  OUVERTURE: null,
  FERMETURE: null,
};

function hasSkillFor(emp: Employee | undefined, mission: string): boolean {
  const req = MISSION_SKILL[mission];
  if (!req) return true;
  return (emp?.skills ?? []).some((s) => s.skill === req);
}

export default function OrganizationPage() {
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState<ViewMode>('day');
  // La période annoncée doit couvrir les mêmes jours que la grille en dessous.
  const { data: pharmacy } = usePharmacySettings();
  const weekSpan = weekDayCount(pharmacy?.openingHours) - 1;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-[22px] font-semibold tracking-tight">Organisation {view === 'day' ? 'du jour' : 'de la semaine'} <PageHelp topic="organization" /></h1>
            <div className="text-[11px] text-slate-500 mt-0.5 tabular-nums capitalize">
              {view === 'day'
                ? format(date, 'EEEE d MMMM yyyy', { locale: fr })
                : `Semaine du ${format(startOfISOWeek(date), 'd MMM', { locale: fr })} au ${format(addDays(startOfISOWeek(date), weekSpan), 'd MMM yyyy', { locale: fr })}`}
            </div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            <button
              onClick={() => setDate((d) => view === 'day' ? addDays(d, -1) : addWeeks(d, -1))}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
              title={view === 'day' ? 'Jour précédent' : 'Semaine précédente'}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setDate(new Date())}
              className="px-2 py-1 rounded-lg hover:bg-slate-100 text-[11px] font-medium text-slate-600"
            >
              Aujourd'hui
            </button>
            <button
              onClick={() => setDate((d) => view === 'day' ? addDays(d, 1) : addWeeks(d, 1))}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"
              title={view === 'day' ? 'Jour suivant' : 'Semaine suivante'}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setView('day')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${
                view === 'day' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              <Calendar size={13} />
              Jour
            </button>
            <button
              onClick={() => setView('week')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] font-medium transition-colors ${
                view === 'week' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
              }`}
            >
              <CalendarDays size={13} />
              Semaine
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {view === 'day' ? <DayView date={date} /> : <WeekView date={date} />}
      </div>
    </div>
  );
}

// ============================================================================
// VUE JOUR
// ============================================================================

function DayView({ date }: { date: Date }) {
  const dateStr = format(date, 'yyyy-MM-dd');
  const { data: allEmployees } = useEmployees();
  const morning = useOrganization(dateStr, true);
  const afternoon = useOrganization(dateStr, false);
  const assignMut = useAssignOrganization();
  const resetMut = useResetOrganization();

  async function handleReset(isMorning: boolean) {
    if (!window.confirm('Réinitialiser cette demi-journée ? Le moteur recalculera une nouvelle proposition automatiquement.')) return;
    await resetMut.mutateAsync({ date: dateStr, isMorning });
  }

  if (morning.isLoading || afternoon.isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-slate-500">Calcul de l'organisation…</div>
      </div>
    );
  }

  const morningData = morning.data as ProposalData | undefined;
  const afternoonData = afternoon.data as ProposalData | undefined;
  // On n'affiche les alertes d'effectif que pour les demi-journées où au moins
  // un salarié est planifié : inutile de crier « sous-effectif » un jour fermé.
  const criticalAlerts = [
    ...(morningData && morningData.presentEmployeeIds.length > 0
      ? morningData.alerts.filter((a) => a.type === 'CRITICAL')
      : []),
    ...(afternoonData && afternoonData.presentEmployeeIds.length > 0
      ? afternoonData.alerts.filter((a) => a.type === 'CRITICAL')
      : []),
  ];
  const dayEmpty =
    (morningData?.presentEmployeeIds.length ?? 0) === 0 &&
    (afternoonData?.presentEmployeeIds.length ?? 0) === 0;
  // Férié fermé : on le nomme, plutôt que « personne de planifié ».
  const holidayName = morningData?.holidayName ?? afternoonData?.holidayName;

  return (
    <div className="p-6">
      {criticalAlerts.length > 0 && (
        <div className="mb-5 bg-danger-bg border border-danger/30 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-danger mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-danger mb-1">
              {criticalAlerts.length} alerte{criticalAlerts.length > 1 ? 's' : ''} d'effectif
            </div>
            <ul className="space-y-0.5">
              {criticalAlerts.map((a, i) => (
                <li key={i} className="text-[11.5px] text-danger">· {a.message}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {holidayName ? (
        <div className="bg-slate-100 border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-3">
            <CalendarOff size={22} className="text-slate-400" />
          </div>
          <div className="text-[14px] font-semibold text-slate-700">Férié · officine fermée</div>
          <div className="text-[12px] text-slate-500 mt-1">{holidayName}</div>
          <div className="text-[11px] text-slate-400 mt-3 max-w-[380px] mx-auto leading-relaxed">
            Rien à organiser ce jour-là. Pour ouvrir, cochez « Ouvert » sur ce jour dans{' '}
            <strong>Paramètres → Jours fériés</strong>.
          </div>
        </div>
      ) : dayEmpty ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <CalendarOff size={22} className="text-slate-400" />
          </div>
          <div className="text-[14px] font-semibold text-slate-700">Personne de planifié ce jour</div>
          <div className="text-[12px] text-slate-500 mt-1.5 max-w-[380px] mx-auto leading-relaxed">
            Journée fermée (ex. dimanche) ou aucune disponibilité déclarée. Renseignez les
            disponibilités des salariés, puis lancez <strong>« Générer le planning »</strong> sur la
            semaine pour organiser la journée.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-5">
          <HalfDayPanel
            title="Matin"
            icon={Sun}
            isMorning={true}
            data={morningData}
            allEmployees={allEmployees ?? []}
            date={dateStr}
            onSave={async (assignments) => assignMut.mutateAsync({ date: dateStr, isMorning: true, assignments })}
            onReset={() => handleReset(true)}
            saving={assignMut.isPending}
          />
          <HalfDayPanel
            title="Après-midi"
            icon={Moon}
            isMorning={false}
            data={afternoonData}
            allEmployees={allEmployees ?? []}
            date={dateStr}
            onSave={async (assignments) => assignMut.mutateAsync({ date: dateStr, isMorning: false, assignments })}
            onReset={() => handleReset(false)}
            saving={assignMut.isPending}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PANEL D'UNE DEMI-JOURNÉE (matin ou après-midi)
// ============================================================================

function HalfDayPanel({
  title, icon: Icon, isMorning, data, allEmployees, date, onSave, onReset, saving,
}: {
  title: string;
  icon: typeof Sun;
  isMorning: boolean;
  data: ProposalData | undefined;
  allEmployees: Employee[];
  date: string;
  onSave: (assignments: { mission: string; employeeIds: string[] }[]) => Promise<any>;
  onReset: () => void;
  saving: boolean;
}) {
  // État local des assignations (pour permettre le drag & drop optimiste)
  const [localAssignments, setLocalAssignments] = useState<Record<string, string[]> | null>(null);
  const [dropError, setDropError] = useState<string | null>(null);
  const [draggingEmpId, setDraggingEmpId] = useState<string | null>(null);
  const { data: timeline } = useOrgTimeline(date, isMorning);

  // Resynchroniser l'affichage avec le serveur à chaque nouvelle donnée : après
  // une sauvegarde (drag) OU un « Recalculer », on abandonne l'état optimiste
  // local pour refléter exactement l'état persisté renvoyé par le backend.
  useEffect(() => { setLocalAssignments(null); }, [data]);

  const assignments = useMemo(() => {
    if (!data) return [];
    // Ne garder que les missions effectivement configurées (targetCount > 0)
    const active = data.assignments.filter((a) => a.targetCount > 0);
    if (localAssignments) {
      return active.map((a) => ({ ...a, employeeIds: localAssignments[a.mission] ?? [] }));
    }
    return active;
  }, [data, localAssignments]);

  const presentEmployees = useMemo(() => {
    if (!data) return [];
    return allEmployees.filter((e) => data.presentEmployeeIds.includes(e.id));
  }, [data, allEmployees]);

  // Salariés présents mais non assignés (dispo)
  const assignedIds = useMemo(() => {
    const all = new Set<string>();
    assignments.forEach((a) => a.employeeIds.forEach((id) => all.add(id)));
    return all;
  }, [assignments]);

  const unassignedEmployees = useMemo(
    () => presentEmployees.filter((e) => !assignedIds.has(e.id)),
    [presentEmployees, assignedIds]
  );

  // Ouverture/fermeture = exigences de présence (pas des postes à affecter).
  const presenceChecks = useMemo(() => assignments.filter((a) => a.presence), [assignments]);
  const realMissions = useMemo(() => assignments.filter((a) => !a.presence), [assignments]);

  // Poste comptoir : l'organisation du jour se lit, elle ne se réaffecte pas.
  // Répartir les missions est une décision du titulaire.
  const readOnly = useCounterReadOnly();

  const handleDrop = useCallback(async (targetMission: string, empId: string) => {
    // Le dépôt termine le glisser. On ne compte pas sur `dragend` : la pastille
    // glissée quitte sa mission au dépôt, et l'événement se perd avec elle —
    // les autres missions restaient alors grisées « compétence requise ».
    setDraggingEmpId(null);
    if (readOnly) return;
    // Blocage compétence : on ne peut affecter à une mission que si le salarié
    // en a la compétence (comme le moteur). Le vivier « Disponibles » reste libre.
    if (targetMission !== '__unassigned__') {
      const emp = allEmployees.find((e) => e.id === empId);
      if (!hasSkillFor(emp, targetMission)) {
        setDropError(`${emp?.firstName ?? 'Ce salarié'} n'a pas la compétence « ${MISSION_LABELS[targetMission] ?? targetMission} ». Ajoutez-la dans sa fiche › Compétences.`);
        return;
      }
    }
    setDropError(null);

    // Retirer l'emp de toutes les missions, puis l'ajouter à la target
    const current = localAssignments ?? Object.fromEntries(data!.assignments.map((a) => [a.mission, [...a.employeeIds]]));
    const next: Record<string, string[]> = {};
    for (const [m, ids] of Object.entries(current)) {
      next[m] = ids.filter((id) => id !== empId);
    }
    if (targetMission !== '__unassigned__') {
      if (!next[targetMission]) next[targetMission] = [];
      next[targetMission].push(empId);
    }
    setLocalAssignments(next);

    // Sauvegarder au serveur
    const payload = Object.entries(next).map(([mission, employeeIds]) => ({ mission, employeeIds }));
    await onSave(payload);
  }, [data, localAssignments, onSave, allEmployees, readOnly]);

  if (!data) return null;

  const isPersisted = data.source === 'PERSISTED';

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50/50">
        <div className="flex items-center gap-2">
          <Icon size={15} className="text-slate-500" />
          <div className="text-[13px] font-semibold text-slate-900">{title}</div>
          <Chip variant={isPersisted ? 'primary' : 'slate'} size="sm">
            {isPersisted ? 'Personnalisé' : 'Auto'}
          </Chip>
          {saving && <div className="text-[10px] text-slate-500">Sauvegarde…</div>}
        </div>
        {!readOnly && (
          <button
            onClick={onReset}
            className="text-[10.5px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
            title="Réinitialiser avec la proposition du moteur"
          >
            <RefreshCw size={11} />
            Recalculer
          </button>
        )}
      </div>

      {dropError && (
        <div className="mx-3 mt-2 bg-danger-bg border border-danger/30 rounded-lg px-2.5 py-1.5 text-[11px] text-danger flex items-start gap-1.5">
          <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
          <span className="flex-1">{dropError}</span>
          <button onClick={() => setDropError(null)} className="text-danger/70 hover:text-danger font-bold">×</button>
        </div>
      )}

      {data.closed ? (
        <div className="p-8 text-center">
          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
            <CalendarOff size={16} className="text-slate-400" />
          </div>
          <div className="text-[12px] font-medium text-slate-600">Pharmacie fermée {isMorning ? 'le matin' : "l'après-midi"}</div>
          <div className="text-[10.5px] text-slate-400 mt-1 max-w-[220px] mx-auto">
            Aucune organisation ce créneau. Modifiable dans <strong>Paramètres → Horaires d'ouverture</strong>.
          </div>
        </div>
      ) : presentEmployees.length === 0 ? (
        <div className="p-8 text-center">
          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-2">
            <CalendarOff size={16} className="text-slate-400" />
          </div>
          <div className="text-[12px] font-medium text-slate-600">Personne de planifié</div>
          <div className="text-[10.5px] text-slate-400 mt-1 max-w-[220px] mx-auto">
            Aucune disponibilité déclarée ce créneau.
          </div>
        </div>
      ) : (
        <>
          {/* Ouverture / fermeture : exigence de présence (pas un poste dédié) */}
          {presenceChecks.length > 0 && (
            <div className="px-3 pt-3 space-y-1.5">
              {presenceChecks.map((pc) => (
                <PresenceCheckRow
                  key={pc.mission}
                  mission={pc.mission}
                  presentCount={pc.presentCount ?? presentEmployees.length}
                  targetCount={pc.targetCount}
                  status={pc.status}
                />
              ))}
            </div>
          )}

          {/* Missions à affecter */}
          <div className="p-3 space-y-1.5">
            {realMissions.length === 0 ? (
              <div className="py-6 text-center text-[11.5px] text-slate-500">
                Aucune mission configurée pour cette demi-journée. Allez dans <strong>Paramètres → Effectifs</strong> pour en créer.
              </div>
            ) : (
              realMissions.map((a) => (
                <MissionRow
                  key={a.mission}
                  mission={a.mission}
                  targetCount={a.targetCount}
                  minCount={a.minCount ?? 0}
                  status={a.status}
                  employeeIds={a.employeeIds}
                  eligibleCount={a.eligibleCount}
                  allEmployees={allEmployees}
                  draggingEmpId={draggingEmpId}
                  onDragStartEmp={setDraggingEmpId}
                  onDragEndEmp={() => setDraggingEmpId(null)}
                  onDrop={(empId) => handleDrop(a.mission, empId)}
                />
              ))
            )}
          </div>

          {/* Couverture horaire (tranches de 30 min) */}
          {timeline && !timeline.closed && (
            <div className="px-3 pb-3 pt-1 border-t border-slate-100">
              <TimelineStrip timeline={timeline} />
            </div>
          )}

          {/* Salariés présents non assignés */}
          <div className="border-t border-slate-200 bg-slate-50/50 p-3">
            <div
              className="text-[10px] uppercase tracking-wider font-medium text-slate-500 mb-2 flex items-center gap-1.5 cursor-help"
              title="Salariés qui travaillent ce créneau (présents dans le planning) mais pas encore affectés à une mission. Glissez-les sur une mission pour les assigner."
            >
              <Users size={10} />
              Disponibles ({unassignedEmployees.length})
              <span className="normal-case tracking-normal text-slate-400 font-normal">— présents non affectés</span>
            </div>
            <UnassignedDropZone onDrop={(empId) => handleDrop('__unassigned__', empId)}>
              {unassignedEmployees.length === 0 ? (
                <div className="text-[11px] text-slate-400 italic py-1">Tout le monde est assigné.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {unassignedEmployees.map((e) => (
                    <DraggableEmployee key={e.id} employee={e} onDragStartEmp={setDraggingEmpId} onDragEndEmp={() => setDraggingEmpId(null)} />
                  ))}
                </div>
              )}
            </UnassignedDropZone>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// LIGNE DE MISSION (drop target)
// ============================================================================

function MissionRow({
  mission, targetCount, minCount, status, employeeIds, eligibleCount, allEmployees, draggingEmpId, onDragStartEmp, onDragEndEmp, onDrop,
}: {
  mission: string;
  targetCount: number;
  minCount: number;
  status: 'COVERED' | 'PARTIAL' | 'UNCOVERED';
  employeeIds: string[];
  eligibleCount?: number;
  allEmployees: Employee[];
  draggingEmpId: string | null;
  onDragStartEmp: (id: string) => void;
  onDragEndEmp: () => void;
  onDrop: (empId: string) => void;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const employees = allEmployees.filter((e) => employeeIds.includes(e.id));

  const reqSkill = MISSION_SKILL[mission];
  const dragging = !!draggingEmpId;
  const eligible = hasSkillFor(allEmployees.find((e) => e.id === draggingEmpId), mission);
  const blocked = dragging && !eligible; // salarié glissé sans la compétence requise

  const statusColor = {
    COVERED: 'border-green-300 bg-green-50/30',
    PARTIAL: 'border-amber-300 bg-amber-50/30',
    UNCOVERED: 'border-danger/40 bg-danger-bg/30',
  }[status];

  const badgeVariant = {
    COVERED: 'green',
    PARTIAL: 'amber',
    UNCOVERED: 'red',
  }[status] as 'green' | 'amber' | 'red';

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!blocked) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const empId = e.dataTransfer.getData('text/plain');
        if (empId) onDrop(empId);
      }}
      className={`rounded-lg border ${statusColor} transition-all ${isDragOver && !blocked ? 'ring-2 ring-primary scale-[1.01]' : ''} ${blocked ? 'opacity-35' : dragging && eligible ? 'ring-1 ring-green-400' : ''}`}
    >
      <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-200/60">
        <div className="text-[11.5px] font-semibold">{MISSION_LABELS[mission] ?? mission}</div>
        {reqSkill && eligibleCount !== undefined && (
          <span
            className={`text-[9.5px] font-medium tabular-nums ${eligibleCount < targetCount ? 'text-amber-600' : 'text-slate-400'}`}
            title="Nombre de salariés présents ce créneau ayant la compétence requise pour cette mission."
          >
            {eligibleCount} compétent{eligibleCount > 1 ? 's' : ''} présent{eligibleCount > 1 ? 's' : ''}
          </span>
        )}
        <div className="flex-1" />
        {reqSkill && blocked && <span className="text-[9px] font-medium text-danger uppercase tracking-wide">compétence requise</span>}
        {status === 'UNCOVERED' && (
          <Chip variant={badgeVariant} size="sm">
            {employees.length}/{minCount} min
          </Chip>
        )}
        {status === 'PARTIAL' && (
          <Chip variant={badgeVariant} size="sm">
            {employees.length}/{targetCount}
          </Chip>
        )}
        {status === 'COVERED' && (
          <Chip variant={badgeVariant} size="sm">
            ✓ {employees.length}/{targetCount}
          </Chip>
        )}
      </div>
      <div className="p-2 min-h-[44px]">
        {employees.length === 0 ? (
          <div className="text-[10.5px] text-slate-400 italic py-1.5 text-center">Personne assigné</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {employees.map((e) => (
              <DraggableEmployee key={e.id} employee={e} onDragStartEmp={onDragStartEmp} onDragEndEmp={onDragEndEmp} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// EXIGENCE DE PRÉSENCE (ouverture / fermeture) — pas un poste à affecter
// ============================================================================

function PresenceCheckRow({
  mission, presentCount, targetCount, status,
}: {
  mission: string;
  presentCount: number;
  targetCount: number;
  status: 'COVERED' | 'PARTIAL' | 'UNCOVERED';
}) {
  const ok = status === 'COVERED';
  const color = ok
    ? 'border-green-300 bg-green-50/40 text-green-800'
    : status === 'PARTIAL'
    ? 'border-amber-300 bg-amber-50/40 text-amber-800'
    : 'border-danger/40 bg-danger-bg/30 text-danger';
  const Icon = mission === 'OUVERTURE' ? DoorOpen : DoorClosed;
  return (
    <div
      className={`rounded-lg border px-3 py-2 flex items-center gap-2 ${color}`}
      title="Nombre de salariés qui doivent être présents pour ouvrir / fermer. Ils tiennent ensuite d'autres missions — ce n'est pas un poste dédié."
    >
      <Icon size={13} className="flex-shrink-0" />
      <div className="text-[11.5px] font-semibold flex-1">{MISSION_LABELS[mission] ?? mission}</div>
      <div className="text-[10.5px] tabular-nums font-medium">
        {presentCount}/{targetCount} présents {ok ? '✓' : '⚠'}
      </div>
    </div>
  );
}

// ============================================================================
// COUVERTURE HORAIRE — bande de temps + creux détectés
// ============================================================================

function TimelineStrip({ timeline }: { timeline: HalfDayTimeline }) {
  const toMin = (t: string) => {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  if (!timeline.open || !timeline.close) return null;
  const start = toMin(timeline.open);
  const end = toMin(timeline.close);
  const total = Math.max(1, end - start);
  const segColor = (w: 'COVERED' | 'PARTIAL' | 'UNCOVERED') =>
    w === 'COVERED' ? 'bg-emerald-400' : w === 'PARTIAL' ? 'bg-amber-400' : 'bg-red-400';
  // Répartition faite à la main : la couverture suit les missions affectées
  // ci-dessus, pas la proposition du moteur.
  const manual = timeline.basis === 'MANUAL';

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div
          className="text-[10px] uppercase tracking-wider font-medium text-slate-500 flex items-center gap-1.5"
          title={
            manual
              ? 'Couverture selon votre répartition des missions et les horaires de chacun : un salarié ne tient que la mission où vous l’avez placé.'
              : 'Couverture selon les salariés présents et leurs horaires, répartis par le moteur. Dès que vous déplacez quelqu’un, elle suit votre répartition.'
          }
        >
          <Clock size={10} /> Couverture horaire{' '}
          <span className="normal-case tracking-normal text-slate-400 font-normal">
            · {manual ? 'selon votre répartition' : 'selon les présents'}
          </span>
        </div>
        <div className="text-[10px] text-slate-400 tabular-nums">présents {timeline.presentLow}–{timeline.presentPeak}</div>
      </div>

      <div className="flex h-6 rounded-md overflow-hidden border border-slate-200">
        {timeline.segments.map((s, i) => {
          const w = ((toMin(s.to) - toMin(s.from)) / total) * 100;
          const title =
            `${s.from}–${s.to} · ${s.presentCount} présent(s)\n` +
            s.missions.map((m) => `${MISSION_LABELS[m.mission] ?? m.mission} : ${m.have}/${m.targetCount}`).join('\n');
          return (
            <div
              key={i}
              style={{ width: `${w}%` }}
              title={title}
              className={`${segColor(s.worst)} flex items-center justify-center text-[9px] font-semibold text-white border-r border-white/40 last:border-r-0`}
            >
              {s.presentCount}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] text-slate-400 tabular-nums mt-0.5">
        <span>{timeline.open}</span>
        <span>{timeline.close}</span>
      </div>

      {timeline.gaps.length > 0 ? (
        <div className="mt-2 space-y-1">
          {timeline.gaps.map((g, i) => (
            <div
              key={i}
              className={`text-[10.5px] flex items-center gap-1.5 ${g.level === 'CRITICAL' ? 'text-danger' : 'text-amber-700'}`}
            >
              <AlertTriangle size={11} className="flex-shrink-0" />
              <strong>{MISSION_LABELS[g.mission] ?? g.mission}</strong>
              <span className="tabular-nums">{g.from}–{g.to}</span>
              <span className="text-slate-500">
                {g.level === 'CRITICAL' ? `${g.have}/${g.minCount} min` : `${g.have}/${g.targetCount} cible`}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-2 text-[10.5px] text-emerald-700 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Couverture stable sur tout le créneau
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SALARIÉ DRAGGABLE
// ============================================================================

function DraggableEmployee({ employee, onDragStartEmp, onDragEndEmp }: { employee: Employee; onDragStartEmp?: (id: string) => void; onDragEndEmp?: () => void }) {
  const readOnly = useCounterReadOnly();
  const skills = (employee.skills ?? []).map((s) => s.skill).join(', ');
  return (
    <div
      draggable={!readOnly}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', employee.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStartEmp?.(employee.id);
      }}
      onDragEnd={() => onDragEndEmp?.()}
      title={skills ? `Compétences : ${skills}` : 'Aucune compétence déclarée'}
      className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 bg-white border border-slate-200 rounded-full cursor-grab active:cursor-grabbing hover:border-primary hover:shadow-sm transition-all group"
    >
      <div
        className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8.5px] font-semibold"
        style={{ backgroundColor: employee.color }}
      >
        {employee.firstName[0]}{employee.lastName[0]}
      </div>
      <span className="text-[10.5px] font-medium text-slate-800">
        {employee.firstName} {employee.lastName.charAt(0)}.
      </span>
    </div>
  );
}

// ============================================================================
// ZONE DE DROP "NON ASSIGNÉS"
// ============================================================================

function UnassignedDropZone({ children, onDrop }: { children: React.ReactNode; onDrop: (empId: string) => void }) {
  const [isDragOver, setIsDragOver] = useState(false);

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        const empId = e.dataTransfer.getData('text/plain');
        if (empId) onDrop(empId);
      }}
      className={`rounded-lg transition-all ${isDragOver ? 'bg-primary-bg/50 ring-2 ring-primary' : ''}`}
    >
      {children}
    </div>
  );
}

// ============================================================================
// VUE SEMAINE
// ============================================================================

function WeekView({ date }: { date: Date }) {
  const { data: pharmacy } = usePharmacySettings();
  // Autant de colonnes que de jours d'ouverture : le dimanche s'ajoute quand
  // l'officine ouvre ce jour-là.
  const dayCount = weekDayCount(pharmacy?.openingHours);
  const weekStart = startOfISOWeek(date);
  const days = Array.from({ length: dayCount }, (_, i) => addDays(weekStart, i));

  return (
    <div className="p-6">
      <div className="bg-primary-bg/40 border border-primary/20 rounded-xl p-4 mb-5 text-[12px] text-slate-700 flex items-start gap-3">
        <UserCheck size={16} className="text-primary mt-0.5" />
        <div>
          La vue semaine affiche un aperçu synthétique pour les {dayCount} jours ouvrés. Pour modifier les assignations (drag &amp; drop), passez en <strong>vue Jour</strong>.
        </div>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${dayCount}, 1fr)` }}>
        {days.map((d) => {
          const isToday = format(d, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
          return (
            <WeekDayColumn key={d.toISOString()} date={d} isToday={isToday} />
          );
        })}
      </div>
    </div>
  );
}

function WeekDayColumn({ date, isToday }: { date: Date; isToday: boolean }) {
  const dateStr = format(date, 'yyyy-MM-dd');
  const { data: allEmployees } = useEmployees();
  const morning = useOrganization(dateStr, true);
  const afternoon = useOrganization(dateStr, false);

  return (
    <div className={`bg-white border rounded-xl overflow-hidden ${isToday ? 'border-primary shadow-sm' : 'border-slate-200'}`}>
      <div className={`px-3 py-2 text-center border-b ${isToday ? 'bg-primary-bg/50 border-primary/20' : 'bg-slate-50/50 border-slate-200'}`}>
        <div className={`text-[11px] font-medium capitalize ${isToday ? 'text-primary' : 'text-slate-900'}`}>
          {format(date, 'EEEE', { locale: fr })}
        </div>
        <div className="text-[10px] text-slate-500 tabular-nums">{format(date, 'd MMM', { locale: fr })}</div>
      </div>

      <div>
        <WeekHalfDay title="Matin" icon={Sun} data={morning.data as ProposalData | undefined} allEmployees={allEmployees ?? []} />
        <WeekHalfDay title="Aprem" icon={Moon} data={afternoon.data as ProposalData | undefined} allEmployees={allEmployees ?? []} />
      </div>
    </div>
  );
}

function WeekHalfDay({ title, icon: Icon, data, allEmployees }: { title: string; icon: typeof Sun; data: ProposalData | undefined; allEmployees: Employee[] }) {
  if (!data) return <div className="p-2 text-[10px] text-slate-400">…</div>;

  if (data.closed) {
    return (
      <div className="p-2 border-b border-slate-100 last:border-b-0">
        <div className="flex items-center gap-1 mb-1">
          <Icon size={10} className="text-slate-300" />
          <div className="text-[9px] uppercase tracking-wider text-slate-400 font-medium flex-1">{title}</div>
        </div>
        <div className="text-[9.5px] text-slate-400 italic truncate" title={data.holidayName ? `Férié : ${data.holidayName}` : undefined}>
          {data.holidayName ? `Férié · ${data.holidayName}` : 'Fermé'}
        </div>
      </div>
    );
  }

  const hasCritical = data.alerts.some((a) => a.type === 'CRITICAL');
  // On masque ouverture/fermeture (exigences de présence) des pastilles missions.
  const activeMissions = data.assignments.filter((a) => a.targetCount > 0 && !a.presence);

  return (
    <div className="p-2 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-1 mb-1.5">
        <Icon size={10} className="text-slate-500" />
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-medium flex-1">{title}</div>
        {hasCritical && <AlertTriangle size={9} className="text-danger" />}
      </div>
      {activeMissions.length === 0 ? (
        <div className="text-[9.5px] text-slate-400 italic">—</div>
      ) : (
        <div className="space-y-1">
          {activeMissions.slice(0, 5).map((a) => {
            const emps = allEmployees.filter((e) => a.employeeIds.includes(e.id));
            return (
              <div key={a.mission} className="flex items-start gap-1">
                <div className={`w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0 ${
                  a.status === 'COVERED' ? 'bg-green-500' : a.status === 'PARTIAL' ? 'bg-amber-500' : 'bg-danger'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] text-slate-600 font-medium truncate">{MISSION_LABELS[a.mission]}</div>
                  {emps.length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-0.5">
                      {emps.slice(0, 3).map((e) => (
                        <div key={e.id} className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-white text-[6.5px] font-semibold" style={{ backgroundColor: e.color }} title={`${e.firstName} ${e.lastName}`}>
                          {e.firstName[0]}
                        </div>
                      ))}
                      {emps.length > 3 && <div className="text-[8px] text-slate-500">+{emps.length - 3}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {activeMissions.length > 5 && (
            <div className="text-[8.5px] text-slate-400">+{activeMissions.length - 5} autres</div>
          )}
        </div>
      )}
    </div>
  );
}
